import React, { type PropsWithChildren, useEffect, useMemo } from 'react'
import { PlanTab } from '../PlanTab'
import { Modal } from '../../../components/Modal/Modal'
import { useZocksAuth, useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useGetBillingCustomerQuery } from '../../../store/plan/plan'
import { selectAccountId, selectAuthenticatedUserToken } from '../../../store/auth/authSlice'
import { LayoutWithLeftSidebarDummy } from '../../LayoutWithLeftSidebar/LayoutWithLeftSidebar'
import { readToken, Token } from '../../../common/token'
import { refreshLock } from '../../../common/baseQuery'
import { refreshToken } from '../../../store/auth/authActions'

export const LockScreenWrapper: React.FC<PropsWithChildren> = ({ children }) => {
  const userToken = useAppSelector(selectAuthenticatedUserToken)

  const isInitialToken = useMemo(() => {
    return userToken ? Token.fromSerialized(userToken).isInitialToken : false
  }, [userToken])

  if (!isInitialToken) {
    return children
  }

  return <LockScreen />
}

const LockScreen: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { hasScope } = useZocksAuth()
  const [searchParams] = useSearchParams()

  const checkoutSuccess = !!searchParams.get('checkoutSuccess')

  const hasBillingCustomerReadPermission = hasScope('**', 'billing_customer:read', ['billing_customer:admin'])
  const hasBillingSubscriptionChangePermission = hasScope('**', 'billing_subscription:change', ['billing_subscription:admin'])

  const { data: customer } = useGetBillingCustomerQuery({ accountId }, { skip: !hasBillingCustomerReadPermission || !hasBillingSubscriptionChangePermission })

  const canStartFreeTrial = !customer?.data.internalMetadata?.cannotStartFreeTrial
  const hasSubscription = !!customer?.data.subscription

  useEffect(() => {
    if (!hasSubscription) {
      return
    }

    const token = readToken()
    if (token?.isRenewable) {
      refreshLock(() => dispatch(refreshToken({ accountId: token.accountId, refreshToken: token.refreshToken! }))).catch(console.error)
    }
  }, [dispatch, hasSubscription])

  const body = checkoutSuccess && canStartFreeTrial ? <PollingNoCurrentPlan /> : hasBillingSubscriptionChangePermission ? <PlanTab /> : <CsicskaNoCurrentPlan />

  return (
    <>
      <Modal show header={<></>} body={body} size='xl' showFooterActions={false} centered />
      <LayoutWithLeftSidebarDummy />
    </>
  )
}

const CsicskaNoCurrentPlan = () => {
  return (
    <Container className='p-0 zocks-mb-3'>
      <Row>
        <Col>
          <div className='p-4 zocks-border-0.5 mb-3 zocks-bg-color-warning'>
            <div>
              <h5>Your subscription has expired or been canceled.</h5>
              Please contact your account owner about your Zocks subscription or contact us at{' '}
              <a target='_blank' href='mailto:support@zocks.io' className='button-link-medium zocks-text-gray-900'>
                support@zocks.io
              </a>
              .
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  )
}

const PollingNoCurrentPlan = () => {
  const accountId = useAppSelector(selectAccountId)

  const pollingInterval = 3000

  useZocksQuota({ pollingInterval, refetchQuota: true })
  useGetBillingCustomerQuery({ accountId }, { pollingInterval })

  return (
    <Container className='p-0 zocks-mb-3'>
      <Row>
        <Col>
          <div className='p-4 zocks-border-0.5 zocks-bg-color-success-background'>
            <div className='d-flex flex-column gap-2'>
              <h5>Setting up your subscription</h5>
              Please wait while we set up your subscription.
              <Spinner />
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
